import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { AlertTriangle, FileWarning } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IncidentReportButton = ({ sessionId, volunteerId, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [category, setCategory] = useState("");
  const [action, setAction] = useState("");
  const [notes, setNotes] = useState("");

  const categories = [
    { value: "self_harm_ideation", label: "Self-harm ideation" },
    { value: "harm_to_others", label: "Harm to others" },
    { value: "clinical_request", label: "Clinical/medical request" },
    { value: "emergency_escalation", label: "Emergency escalation" },
    { value: "other_safety_concern", label: "Other safety concern" }
  ];

  const actions = [
    { value: "displayed_escalation_modal", label: "Displayed escalation resources" },
    { value: "988_recommended", label: "Recommended 988 Crisis Line" },
    { value: "911_recommended", label: "Recommended 911" },
    { value: "session_paused", label: "Paused session" },
    { value: "volunteer_disengaged", label: "Disengaged per protocol" }
  ];

  const handleSubmit = async () => {
    if (!category || !action) {
      toast.error("Please select a category and action taken");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/incidents`, {
        session_id: sessionId,
        trigger_type: "volunteer_initiated",
        trigger_category: category,
        action_taken: action,
        volunteer_id: volunteerId,
        system_notes: notes || null
      });
      
      toast.success("Incident logged successfully");
      setIsOpen(false);
      setCategory("");
      setAction("");
      setNotes("");
    } catch (error) {
      toast.error("Failed to log incident");
      console.error("Incident logging error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size={compact ? "sm" : "default"}
        onClick={() => setIsOpen(true)}
        className="text-amber-600 border-amber-300 hover:bg-amber-50"
        data-testid="report-incident-btn"
      >
        <FileWarning className={`${compact ? "w-4 h-4" : "w-4 h-4 mr-2"}`} />
        {!compact && "Report Incident"}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Log Safety Incident
            </DialogTitle>
            <DialogDescription>
              Document this incident for safety review. This helps protect users and volunteers.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Category Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Incident Category *</Label>
              <RadioGroup value={category} onValueChange={setCategory}>
                {categories.map((cat) => (
                  <div key={cat.value} className="flex items-center space-x-3 p-2 rounded hover:bg-muted">
                    <RadioGroupItem value={cat.value} id={cat.value} />
                    <Label htmlFor={cat.value} className="cursor-pointer flex-1">
                      {cat.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Action Taken */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Action Taken *</Label>
              <RadioGroup value={action} onValueChange={setAction}>
                {actions.map((act) => (
                  <div key={act.value} className="flex items-center space-x-3 p-2 rounded hover:bg-muted">
                    <RadioGroupItem value={act.value} id={act.value} />
                    <Label htmlFor={act.value} className="cursor-pointer flex-1">
                      {act.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-semibold">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any relevant context..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting || !category || !action}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting ? "Logging..." : "Log Incident"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IncidentReportButton;
